import { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { hasPlausibleTld } from '../lib/plausibleWebsiteHostname';

/**
 * Bild eines Kunden für Liste und Editor.
 *
 * Reihenfolge: selbst hochgeladenes Logo → Favicon der Website → Platzhalter.
 * Beim Favicon zuerst DuckDuckGo (holt es live von der Seite), dann die Seite selbst.
 * KEIN Google s2/favicons: liefert für Domains ohne eigenes Favicon ein generisches
 * Platzhalter-Icon zurück — mit HTTP 404, aber gültigem Bild-Body.
 *
 * DuckDuckGo hat dasselbe Problem: für unbekannte Domains liefert icons.duckduckgo.com
 * HTTP 404 mit demselben statischen generischen PNG (immer exakt 48×48 Px), ein echtes
 * gefundenes Favicon dagegen mit HTTP 200 und abweichender Größe. Ein <img> prüft nur, ob
 * sich der Body dekodieren lässt, nicht den HTTP-Status — das generische Icon wird also
 * gerendert, `onError` feuert nie. Der Status ist clientseitig wegen fehlendem
 * Access-Control-Allow-Origin nicht auslesbar; `naturalWidth`/`naturalHeight` dagegen sind
 * auch ohne CORS verfügbar — die 48×48-Signatur des generischen Icons wird deshalb in
 * `onLoad` erkannt und wie ein Fehlschlag behandelt (nächster Kandidat).
 */
const DUCKDUCKGO_FALLBACK_SIZE = 48;
export function CustomerLogo({ website, logoUrl, size = 'w-9 h-9' }: {
  website?: string;
  logoUrl?: string;
  size?: string;
}) {
  const hostname = useMemo(() => {
    if (!website) return null;
    try {
      return new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`).hostname;
    } catch {
      return null;
    }
  }, [website]);

  const candidates = useMemo(() => {
    if (logoUrl) return [logoUrl];
    if (!hostname || !hasPlausibleTld(hostname)) return [];
    return [
      `https://icons.duckduckgo.com/ip3/${hostname}.ico`,
      `https://${hostname}/favicon.ico`,
    ];
  }, [hostname, logoUrl]);

  // Bei neuer Quelle wieder vorne anfangen — sonst bleibt ein alter Fehlversuch stehen.
  const [index, setIndex] = useState(0);
  const [fuer, setFuer] = useState(candidates[0]);
  if (fuer !== candidates[0]) {
    setFuer(candidates[0]);
    setIndex(0);
  }

  if (index >= candidates.length) {
    return (
      <div className={`${size} rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0`}>
        <Building2 className="w-4 h-4 text-slate-400 dark:text-slate-500" />
      </div>
    );
  }
  const isDuckDuckGo = candidates[index].startsWith('https://icons.duckduckgo.com/');
  return (
    <img
      key={candidates[index]}
      src={candidates[index]}
      alt=""
      onError={() => setIndex(i => i + 1)}
      onLoad={(e) => {
        const img = e.currentTarget;
        if (isDuckDuckGo && img.naturalWidth === DUCKDUCKGO_FALLBACK_SIZE && img.naturalHeight === DUCKDUCKGO_FALLBACK_SIZE) {
          setIndex(i => i + 1);
        }
      }}
      className={`${size} rounded-lg object-contain shrink-0 bg-white`}
    />
  );
}
